/**
 * Feature copy for the landing page. Every line maps to code in the desktop
 * repo (orkhanart/apollo). Keep it that way: add a feature here only after it
 * runs end to end.
 */
export type FeatureId = 'local' | 'params' | 'tools' | 'layers' | 'versions' | 'graph' | 'agent' | 'cloud'

export interface FeatureGroup {
  /** Picks the card's illustration in FeatureVisuals. */
  id: FeatureId
  title: string
  tag: string
  lead: string
  specs: readonly string[]
  note?: string
}

export const FEATURE_GROUPS: readonly FeatureGroup[] = [
  {
    id: 'local',
    title: 'Generation on your Mac',
    tag: 'local',
    lead: 'ComfyUI is installed for you on first launch and runs as a supervised background process. You never see a node graph unless you want to.',
    specs: [
      'Z-Image Turbo',
      'FLUX.2 klein, edit with words',
      'SDXL, SDXL Turbo, SD 1.5',
      'video: Wan 2.2',
      'music: ACE-Step',
      '3D: TRELLIS.2 (experimental on Mac)',
      'erase, remove background, upscale',
      'optional MLX speed path (MFLUX) for text to image',
    ],
    note: 'The setup wizard reads your chip, memory and free disk, then preselects what fits and leaves 20 GB free.',
  },
  {
    id: 'params',
    title: 'Pro controls when you want them',
    tag: 'parameters',
    lead: 'Plain words by default. Open the advanced panel, or type a slash command, and every knob is there with ranges tuned per model family.',
    specs: [
      'steps',
      'CFG',
      'seed lock',
      'negative prompt',
      'sampler',
      'scheduler',
      'LoRA stack with weights',
      'CLIP skip',
      'VAE override',
      'aspect ratio',
      'count',
      'sweeps over seed, steps or guidance',
      '/ratio  /seed lock  /lora  /steps  /cfg',
    ],
  },
  {
    id: 'tools',
    title: 'Sixty apps on the canvas',
    tag: 'apps',
    lead: 'Right-click a frame or open Apps. Every edit becomes a new version, so nothing is destructive.',
    specs: [
      'inpaint with a painted mask, expand, erase',
      'quick edit, edit elements, edit text, move object',
      'multi-angles',
      'upscale 2× and 4×',
      'background removal, local or cloud',
      'variations with a creativity slider',
      'brightness, contrast, saturation, hue, temperature',
      'blur, sharpen, black and white',
      'crop with aspect presets, flip and rotate',
      'text overlay, resize',
      'image to video, local or cloud',
    ],
  },
  {
    id: 'layers',
    title: 'Design frames',
    tag: 'layers',
    lead: 'A layer editor for the parts a diffusion model should not touch: type, logos, layout. Genie can build and revise these too.',
    specs: [
      'text, image, rectangle and circle layers',
      'inspector with align, blend modes, stroke',
      'undo and redo',
      'agent creates and updates designs',
      'full-resolution render saved as a version',
    ],
  },
  {
    id: 'versions',
    title: 'Artboards with a memory',
    tag: 'versions',
    lead: 'Every generation and every edit stacks as a version with its parent recorded. Go back, branch, compare.',
    specs: [
      'media, design, web, note, section and composition boards',
      'lineage view',
      'drag in files, paste from clipboard',
      'export PNG, JPG or WebP with prompt and seed',
      'SQLite document at ~/.genie',
      'images in ~/.genie/outputs',
    ],
  },
  {
    id: 'graph',
    title: 'Automations',
    tag: 'workflow mode',
    lead: 'Switch a canvas to Workflow and the same verbs become blocks. Every session is already recorded as a graph you can replay.',
    specs: [
      'about forty block types',
      'triggers: manual, schedule, watch a folder',
      'canvas in and out',
      'generate, edit, compose',
      'if and merge',
      'read and save files',
      'history graph recorded automatically',
    ],
  },
  {
    id: 'agent',
    title: 'An agent that uses tools',
    tag: 'agent',
    lead: 'Genie plans with its own agent, on the model you connect, and shows each tool call as it happens.',
    specs: [
      'Grok, Claude, OpenAI or OpenRouter',
      'or fully local with Ollama',
      'inspect_canvas, apply_canvas_changes',
      'generate_images, generate_3d, render_3d_views',
      'builds simple ComfyUI workflows via Comfy MCP (optional install)',
      'finds references with licence notes',
      'built-in recipes',
    ],
    note: 'Connect a model in Settings → AI models. With Ollama, planning never leaves your Mac.',
  },
  {
    id: 'cloud',
    title: 'Cloud when you call',
    tag: 'credits or your key',
    lead: 'Heavier models sit next to the local ones. Pay with Genie credits, or bring your own fal.ai or Replicate key.',
    specs: [
      'image: FLUX.2 pro, Nano Banana 2, GPT-Image 2, Seedream 5, Recraft V4',
      'video: Kling v3, Veo 3.1, Sora 2, Seedance 2.5',
      'sound: Stable Audio 2.5, ElevenLabs',
      '3D: Hunyuan3D, Tripo, TRELLIS',
      'rent a cloud GPU by the hour',
    ],
    note: 'Video and sound also run locally; local 3D is experimental on Mac.',
  },
]
